/**
 * Lịch đóng cắt — trình soạn ĐẦY ĐỦ: 3 ngõ ra × 48 timer, mỗi timer có 7 cờ thứ trong tuần
 * và tối đa 10 ngày đặc biệt.
 *
 * ĐIỂM SỐNG CÒN: tủ chạy firmware v1 KHÔNG chở nổi mô hình đầy đủ này. Server tính trước
 * phần sẽ mất và trả về trong `v1Preview.losses` (khi đọc lịch) hoặc trong body HTTP 409
 * (khi đẩy lịch). Màn hình này phải:
 *   1. Hiện thường trực băng vàng liệt kê mất mát của bản đang lưu.
 *   2. Khi đẩy mà nhận 409 → mở hộp thoại liệt kê ĐẦY ĐỦ từng dòng mất mát; chỉ khi người
 *      dùng bấm “Tôi hiểu, vẫn đẩy” mới gọi lại với acceptLosses = true.
 * Tuyệt đối không tự ý nuốt cảnh báo rồi đẩy giúp người dùng.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, CalendarClock, CalendarDays, GripVertical, History, Plus, RotateCcw, Save,
  Trash2, Upload,
} from 'lucide-react';
import { ApiError, EVENT_LENH_ACK, api, errorMessage } from '../lib/api';
import { qk, useSchedule, useScheduleHistory, useTree } from '../lib/queries';
import { useAuth } from '../lib/auth';
import { BangKhoaTuyen, cauKhoaTuyen } from '../components/Khoa';
import {
  Badge, Button, Card, EmptyState, ErrorBox, Modal, Spinner, StatusDot, Switch, TimeInput,
  WarningBanner, useToast,
} from '../components/ui';
import {
  ALL_DAYS, DAY_LABELS, DAY_LABELS_FULL, NO_DAYS, describeDays, durationMin, fmtDateTime,
  fmtDuration, fmtSpecialDay, minToStr, toggleDay,
} from '../lib/format';
import {
  MODE_LABEL, SCHEDULE_STATE_LABEL,
  type FullOutput, type FullTimer, type OutputMode, type SpecialDay, type V1Preview,
} from '../lib/types';

/**
 * 🔴 Ô CHỌN CHẾ ĐỘ PHỤ THUỘC PHIÊN BẢN GIAO THỨC CỦA TỦ (chốt YC-25 mục 2).
 *
 * Tủ chỉ có `fix_time` (TIMER) và `sun_time` (SEASON). `ALWAYS_ON`/`ALWAYS_OFF` KHÔNG tồn tại:
 * gửi xuống tủ v2 thì nhận `ACK_UNSUPPORTED`.
 *
 * ⚠ Vì sao v1 vẫn còn hai chế độ ấy: đường v1 `downgradeToV1()` quy đổi ALWAYS_ON thành timer
 *   00:00–23:59 **và nói ra chỗ mất mát cho người dùng xác nhận trước khi gửi**. Quy đổi có
 *   khai báo thì chấp nhận được; quy đổi im lặng thì không — đọc ngược lại sẽ ra `TIMER`, tức
 *   là gửi một thứ, đọc về một thứ khác.
 *
 * ⛔ ĐỪNG hiện một chế độ mà tủ sẽ từ chối. Một ô chọn hứa nhiều hơn thiết bị làm được thì
 *   người bấm chỉ phát hiện ra sau khi đèn không đổi.
 */
const MODES_V1: OutputMode[] = ['TIMER', 'SEASON', 'ALWAYS_ON', 'ALWAYS_OFF'];
const MODES_V2: OutputMode[] = ['TIMER', 'SEASON'];
const modesTheoTu = (protocolVer: number) => (protocolVer >= 2 ? MODES_V2 : MODES_V1);

export default function SchedulePage() {
  const { cabinetId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  /*
   * ➕ `P-6` `B4` mục 3 — SỬA LỊCH đi bằng CỜ `canEditSchedule` CỦA CHÍNH TUYẾN chứa tủ này.
   * ⚠ Móc phải gọi TRƯỚC mọi `return` sớm (luật hook), nên `routeId` lấy ở dưới không dùng
   *   được ở đây — thay vào đó lấy `coCoTuyen` một lần rồi hỏi khi đã biết tủ.
   */
  const { coCoTuyen } = useAuth();

  const tree = useTree();
  const schedule = useSchedule(cabinetId);
  const history = useScheduleHistory(cabinetId);

  const [outputs, setOutputs] = useState<FullOutput[] | null>(null);
  const [activeOutput, setActiveOutput] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [showAllSlots, setShowAllSlots] = useState(false);
  const [preview, setPreview] = useState<V1Preview | null>(null);
  /** Danh sách mất mát server trả về ở HTTP 409 — mở hộp thoại xác nhận */
  const [pendingLosses, setPendingLosses] = useState<string[] | null>(null);
  /** `commandId` của lần đẩy vừa rồi — BA cái, mỗi ngõ ra một cái (YC-02). */
  const [dangCho, setDangCho] = useState<string[]>([]);
  /** Những ngõ ra tủ TỪ CHỐI, kèm `detail` mang giá trị bị từ chối. */
  const [tuChoi, setTuChoi] = useState<{ commandId: string; ten: string; detail: string }[]>([]);

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * 🔴 NGHE TỦ TRẢ LỜI, NGAY TẠI CHỖ VỪA BẤM NÚT.
   *
   * dev_device cho firmware trả `detail` mang GIÁ TRỊ bị từ chối — *"ngõ 2 ô 12: ngày đặc
   * biệt 0/5"*, *"ghi NVS xong nhưng đọc lại KHÔNG khớp"*. Mười cửa từ chối, mười câu mang số.
   *
   * ⛔ Câu ấy vô dụng nếu nó chỉ về tới CSDL và một bảng nhật ký ở TRANG KHÁC. Người vừa bấm
   *   "Đẩy lịch" đang nhìn MÀN HÌNH NÀY. Đưa câu tới đúng chỗ ấy mới là đóng vòng.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  useEffect(() => {
    const nghe = (e: Event) => {
      const m = (e as CustomEvent).detail as {
        type: string; commandId?: string | null; result?: number; ten?: string; detail?: string | null;
      };
      if (!m?.commandId || !dangCho.includes(m.commandId)) return;
      if (m.type === 'command.timeout') {
        setTuChoi((ds) => [...ds, {
          commandId: m.commandId!, ten: 'KHÔNG TRẢ LỜI',
          detail: 'Tủ không xác nhận trong thời gian chờ. Lịch có thể CHƯA vào tủ.',
        }]);
        return;
      }
      if ((m.result ?? 0) !== 0) {
        setTuChoi((ds) => [...ds, {
          commandId: m.commandId!, ten: m.ten ?? `result=${m.result}`,
          detail: m.detail ?? '(tủ không nói lý do)',
        }]);
      }
    };
    window.addEventListener(EVENT_LENH_ACK, nghe);
    return () => window.removeEventListener(EVENT_LENH_ACK, nghe);
  }, [dangCho]);
  const [specialFor, setSpecialFor] = useState<{ outIdx: number; timerIdx: number } | null>(null);
  /** Khe đang được kéo — `null` khi không kéo. Chỉ số KHE, không phải vị trí trong mảng hiện. */
  const [dangKeo, setDangKeo] = useState<number | null>(null);

  useEffect(() => { document.title = 'Lịch đóng cắt — SDKD'; }, []);

  // Nạp lịch từ server vào trạng thái soạn thảo (chỉ khi chưa sửa dở dang)
  useEffect(() => {
    if (!schedule.data) return;
    if (dirty) return;
    setOutputs(normalize(schedule.data.schedule.outputs));
    setPreview(schedule.data.v1Preview);
  }, [schedule.data, dirty]);

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-07B` 11/09/2026 — ĐẾM SỐ KHE ĐÃ SỬA, không chỉ nói "có thay đổi".
   *
   * 🔴 `dirty` là một cái cờ BOOLEAN: nó biết *có sửa gì đó*, không biết *sửa bao nhiêu*. Trên
   *   một trang có 3 ngõ ra × 48 khe, "có thay đổi chưa lưu" là một câu người đọc KHÔNG kiểm
   *   lại được — họ phải bấm qua ba ngõ ra và rà 144 khe để biết mình vừa động vào cái gì.
   *   Một con số làm câu ấy kiểm được: sửa 1 khe mà đếm ra 7 thì người ta biết mình vừa lỡ tay.
   *
   * ⛔ ĐẾM BẰNG CÁCH SO VỚI BẢN MÁY CHỦ ĐANG GIỮ, không bằng cách tự đếm mỗi lần bấm. Một bộ
   *   đếm cộng dồn theo thao tác sẽ đếm cả lần sửa RỒI SỬA LẠI NHƯ CŨ — và nó không bao giờ về
   *   0 được nữa, kể cả khi hai bản đã giống hệt nhau. So hai bản thì con số luôn là SỰ THẬT
   *   hiện tại, không phải lịch sử bấm nút.
   * 🔴 VÀ CA "CHƯA ĐỌC ĐƯỢC BẢN ĐANG LƯU" TRẢ `null`, KHÔNG TRẢ 0 (Luật số 0): `0` nghĩa là
   *   *"đã so và không khác gì"*, mà lúc ấy ta chưa có gì để so.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  const soKheDaSua = useMemo<number | null>(() => {
    if (!outputs || !schedule.data) return null;
    const goc = normalize(schedule.data.schedule.outputs);
    let n = 0;
    for (let i = 0; i < outputs.length; i += 1) {
      const a = outputs[i]; const b = goc[i];
      if (!b) { n += a.timers.filter((t) => t.enabled).length; continue; }
      /* Sửa ở tầng NGÕ RA (tên · chế độ · offset · cho phép) đếm là MỘT thay đổi của ngõ ấy. */
      if (a.name !== b.name || a.mode !== b.mode || a.enabled !== b.enabled
          || a.sunsetOffsetMin !== b.sunsetOffsetMin || a.sunriseOffsetMin !== b.sunriseOffsetMin) n += 1;
      for (let k = 0; k < a.timers.length; k += 1) {
        const x = a.timers[k]; const y = b.timers[k];
        /*
         * ⚠ SO CẢ KHE TẮT: một khe vừa bị xoá (`enabled: false`) LÀ một thay đổi phải đẩy —
         *   không đẩy thì tủ vẫn chạy cái khe ấy. Bỏ qua khe tắt là giấu đúng loại sửa nguy
         *   hiểm nhất: cái người ta tưởng đã xoá mà đèn vẫn bật.
         */
        if (!y
            || x.enabled !== y.enabled || x.onMin !== y.onMin || x.offMin !== y.offMin
            || x.daysOfWeek !== y.daysOfWeek
            || JSON.stringify(x.specialDays) !== JSON.stringify(y.specialDays)) n += 1;
      }
    }
    return n;
  }, [outputs, schedule.data]);

  const cabinets = useMemo(
    () => (tree.data ?? []).flatMap((r) => r.cabinets.map((c) => ({ ...c, routeName: r.tenDayDu }))),
    [tree.data]
  );
  const currentCabinet = cabinets.find((c) => c.id === cabinetId) ?? null;
  /*
   * 🔴 TUYẾN CỦA TỦ ĐANG MỞ — `null` khi tủ chưa gán tuyến, và ca ấy KHÔNG phải chuyện quyền:
   *   không ai sửa được lịch của một tủ không thuộc tuyến nào. Nói đúng như thế, đừng đổ cho
   *   tài khoản (`cauChanCoTuyen` phân biệt bốn ca).
   */
  const tuyenCuaTu = (tree.data ?? []).find((r) => r.cabinets.some((c) => c.id === cabinetId))?.id ?? null;
  const qdSuaLich = coCoTuyen('canEditSchedule', tuyenCuaTu);
  const canEdit = qdSuaLich === 'cho-qua';
  const protocolVer = schedule.data?.protocolVer ?? currentCabinet?.protocolVer ?? 1;
  /*
   * 🔴 BẢN KHAI CỦA TỦ — tắt ô theo ĐÂY, không suy từ `protocolVer`, không đọc hợp đồng.
   *
   * Hợp đồng có thể đi trước firmware: YC-25 nói tủ không lưu offset mùa, và bốn ngày sau
   * tủ tự khai `supportsSeasonOffset=0` trên dây. Cùng một sự thật, nhưng cái thứ hai **đi
   * theo từng con tủ** — một tủ nâng firmware là ô mở ra ngay, không phải chờ ai sửa mã.
   *
   * ⚠ Chưa khai thì mặc định `false` ⇒ **tắt trước, mở sau khi tủ nói được.** Sai theo hướng
   *   an toàn: thà chưa cho bấm còn hơn cho bấm một nút không có tác dụng.
   */
  const kha = schedule.data?.kha;
  const choSuaOffsetMua = !!kha?.offsetMua;
  const choDungNgayDacBiet = kha ? kha.ngayDacBiet : protocolVer >= 2;

  /* ------------------------------------------------------- thao tác sửa */

  const patchOutput = (outIdx: number, patch: Partial<FullOutput>) => {
    setOutputs((prev) => {
      if (!prev) return prev;
      const next = prev.map((o, i) => (i === outIdx ? { ...o, ...patch } : o));
      return next;
    });
    setDirty(true);
  };

  const patchTimer = (outIdx: number, timerIdx: number, patch: Partial<FullTimer>) => {
    setOutputs((prev) => {
      if (!prev) return prev;
      return prev.map((o, i) => {
        if (i !== outIdx) return o;
        return {
          ...o,
          timers: o.timers.map((t) => (t.index === timerIdx ? { ...t, ...patch } : t)),
        };
      });
    });
    setDirty(true);
  };

  const addTimer = (outIdx: number) => {
    const out = outputs?.[outIdx];
    if (!out) return;
    const free = out.timers.find((t) => !t.enabled);
    if (!free) {
      toast.warning('Đã dùng hết 48 khe timer của ngõ ra này');
      return;
    }
    patchTimer(outIdx, free.index, {
      enabled: true,
      onMin: 18 * 60,
      offMin: 5 * 60,
      daysOfWeek: ALL_DAYS,
      specialDays: [],
    });
  };

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-07B` mục 5 — KÉO THẢ ĐỔI THỨ TỰ KHE, **KHÔNG thêm gói nào**.
   *
   * ⚠ Phiếu ghi *"dnd nhẹ, không thêm gói nếu được; nếu cần `@dnd-kit/core` thì lee cài"*.
   *   Được: HTML5 `draggable` + `onDragStart`/`onDragOver`/`onDrop` có sẵn trong trình duyệt.
   *   ⇒ KHÔNG ghi "CẦN LEE CÀI" cho việc này.
   *
   * 🔴 VÀ ĐÂY KHÔNG PHẢI "ĐỔI CHỖ TRÊN MÀN HÌNH". Số khe LÀ ô NVS trên tủ: khe 3 và khe 7 là
   *   hai ô nhớ thật. Đổi thứ tự nghĩa là **HOÁN ĐỔI NỘI DUNG hai ô ấy**, và cả hai ô đều trở
   *   thành "đã sửa" — cả hai đều phải đẩy xuống tủ. Một bản kéo thả chỉ đổi thứ tự HIỂN THỊ
   *   mà không đổi dữ liệu là bản nguy hiểm nhất: người dùng sắp lại cho gọn, đẩy xuống tủ,
   *   và tủ giữ nguyên thứ tự cũ — không ai sai, không ai biết.
   * ⛔ NÊN HOÁN ĐỔI GIỮ NGUYÊN `index` CỦA TỪNG Ô và tráo phần NỘI DUNG. Tráo cả `index` thì
   *   mảng thôi thẳng hàng với ô nhớ, và mọi phép `find((t) => t.index === …)` trong tệp này
   *   đọc sai ngay lập tức.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  const doiChoKhe = (outIdx: number, tuIdx: number, toiIdx: number) => {
    if (tuIdx === toiIdx) return;
    setOutputs((prev) => {
      if (!prev) return prev;
      return prev.map((o, i) => {
        if (i !== outIdx) return o;
        const a = o.timers.find((t) => t.index === tuIdx);
        const b = o.timers.find((t) => t.index === toiIdx);
        if (!a || !b) return o;
        return {
          ...o,
          timers: o.timers.map((t) => {
            if (t.index === tuIdx) return { ...b, index: tuIdx };
            if (t.index === toiIdx) return { ...a, index: toiIdx };
            return t;
          }),
        };
      });
    });
    setDirty(true);
  };

  const removeTimer = (outIdx: number, timerIdx: number) => {
    patchTimer(outIdx, timerIdx, {
      enabled: false, onMin: 0, offMin: 0, daysOfWeek: '0000000', specialDays: [],
    });
  };

  /* ---------------------------------------------------------- gọi API */

  const saveDraft = useMutation({
    mutationFn: () =>
      api.put<{ ok: boolean; revision: string; v1Preview: V1Preview }>(
        `/api/cabinets/${cabinetId}/schedule`,
        { outputs }
      ),
    onSuccess: (res) => {
      setPreview(res.v1Preview);
      setDirty(false);
      toast.success('Đã lưu nháp lịch', 'Lịch mới CHƯA được đẩy xuống tủ.');
      qc.invalidateQueries({ queryKey: qk.schedule(cabinetId ?? '') });
      qc.invalidateQueries({ queryKey: qk.cabinet(cabinetId ?? '') });
    },
    onError: (e) => toast.error('Không lưu được lịch', errorMessage(e)),
  });

  /**
   * Hỏi tủ đang giữ lịch gì. KHÔNG ghi gì xuống tủ — lệnh đọc, không `commandId`, không ACK.
   *
   * ⛔ KHÔNG gộp kết quả thành một chữ "thành công": đọc được 2/3 ngõ là một kết quả KHÁC HẲN
   * đọc được 3/3, và người bấm phải biết mình đang nhìn cái nào. Ngõ không trả lời thì phần
   * hiển thị của nó vẫn là bản máy chủ — nói ra, đừng để người đọc tưởng đã đối chiếu.
   */
  const docTuTu = useMutation({
    mutationFn: () => api.post<{
      ok: boolean; ngoNhanDuoc: number[]; thieu: number[]; ghiChu: string;
    }>(`/api/cabinets/${cabinetId}/schedule/doc-tu-tu`),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: qk.schedule(cabinetId ?? '') });
      if (r.ok) toast.success('Đã đọc lịch từ tủ', r.ghiChu);
      else toast.warning('Đọc lịch từ tủ CHƯA ĐỦ', r.ghiChu);
    },
    onError: (e) => toast.error('Không đọc được lịch từ tủ', errorMessage(e)),
  });

  const applySchedule = useMutation({
    mutationFn: (acceptLosses: boolean) =>
      api.post<{ ok: boolean; revision: string; commandId: string; commandIds?: string[]; state: string; wireVer: number; losses: string[] }>(
        `/api/cabinets/${cabinetId}/schedule/apply`,
        { outputs, acceptLosses }
      ),
    onSuccess: (res) => {
      setPendingLosses(null);
      setDirty(false);
      /*
       * ⛔ "ĐÃ GỬI", KHÔNG PHẢI "ĐÃ ĐẨY". Câu cũ báo THÀNH CÔNG ngay lúc khung rời máy chủ —
       *   trước khi tủ nói một tiếng nào. Tủ từ chối thì người bấm nút vẫn thấy một dòng
       *   xanh và không bao giờ biết. Sự thật ở thời điểm này là: đã gửi, đang chờ trả lời.
       */
      setDangCho(res.commandIds ?? [res.commandId]);
      setTuChoi([]);
      /*
       * ═══════════════════════════════════════════════════════════════════════════════════
       * 🔴 "KHÔNG MẤT DỮ LIỆU NÀO" IN RA LÚC KHÔNG CÓ KHUNG NÀO ĐƯỢC GỬI — chốt dev_device.
       *
       * `losses` chỉ đếm **mất mát do hạ cấp v1**. Tủ ĐANG OFFLINE thì máy chủ dựng lệnh với
       * `state = 'SKIPPED_OFFLINE'` và **không gửi khung nào** — `losses` vẫn rỗng, và câu
       * cũ khoe *"Truyền trọn vẹn, không mất dữ liệu nào."*
       *
       * ⛔ Hai câu khác hẳn nhau bị gộp làm một:
       *      "gửi đi trọn vẹn"        — có khung, không mất gì
       *      "chưa gửi được gì cả"    — không có khung nào rời máy chủ
       *   Và câu thứ hai đội lốt câu thứ nhất, ở đúng lúc người ta cần biết sự thật nhất.
       * ═══════════════════════════════════════════════════════════════════════════════════
       */
      const chuaGui = res.state === 'SKIPPED_OFFLINE';
      if (chuaGui) {
        toast.error(
          'CHƯA gửi được lịch — tủ đang offline',
          'Lịch đã lưu ở máy chủ và sẽ đẩy khi tủ nối lại. Trên tủ vẫn là lịch CŨ.'
        );
      } else {
        toast.success(
          `Đã gửi lịch xuống tủ (giao thức v${res.wireVer}) — đang chờ tủ xác nhận`,
          res.losses.length
            ? `${res.losses.length} mục không truyền được bằng v1 — xem lại băng cảnh báo vàng.`
            : 'Gửi đi trọn vẹn, không mất mục nào khi hạ cấp.'
        );
      }
      qc.invalidateQueries({ queryKey: qk.schedule(cabinetId ?? '') });
      qc.invalidateQueries({ queryKey: qk.scheduleHistory(cabinetId ?? '') });
      qc.invalidateQueries({ queryKey: qk.cabinet(cabinetId ?? '') });
      qc.invalidateQueries({ queryKey: qk.commands(cabinetId ?? '') });
    },
    onError: (e) => {
      // 409 + mảng losses = server đang HỎI Ý người dùng, không phải lỗi kỹ thuật
      if (e instanceof ApiError && e.status === 409 && e.losses.length > 0) {
        setPendingLosses(e.losses);
        return;
      }
      toast.error('Không đẩy được lịch xuống tủ', errorMessage(e));
    },
  });

  const rollback = useMutation({
    mutationFn: (scheduleId: string) =>
      api.post<{ ok: boolean; revision: string; commandId: string; losses: string[] }>(
        `/api/cabinets/${cabinetId}/schedule/rollback/${scheduleId}`
      ),
    onSuccess: (res) => {
      setDirty(false);
      toast.success(
        'Đã lùi về bản lịch cũ và đẩy xuống tủ',
        res.losses.length ? `${res.losses.length} mục không truyền được bằng v1.` : undefined
      );
      qc.invalidateQueries({ queryKey: qk.schedule(cabinetId ?? '') });
      qc.invalidateQueries({ queryKey: qk.scheduleHistory(cabinetId ?? '') });
    },
    onError: (e) => toast.error('Không lùi được về bản lịch cũ', errorMessage(e)),
  });

  /* -------------------------------------------------------------- vẽ */

  if (!cabinetId) {
    return (
      <div className="space-y-4">
        {/*
          * ⚠ CHƯA CHỌN TỦ ⇒ TIÊU ĐỀ KHÔNG CÓ TÊN TỦ, và đó là câu ĐÚNG ở trạng thái này.
          *   Ghép một cái tên mặc định vào đây (tủ đầu tiên chẳng hạn) là mở ra một trang soạn
          *   lịch cho một con tủ người dùng chưa chọn.
          */}
        <h1 className="chu-tieude m-0 text-[26px] font-semibold leading-tight text-chu-1">
          Lịch đóng cắt
        </h1>
        <Card title="Chọn tủ cần soạn lịch">
          {tree.isLoading && <Spinner />}
          {tree.data && cabinets.length === 0 && <EmptyState title="Chưa có tủ nào trong hệ thống" />}
          {cabinets.length > 0 && (
            <select className="inp max-w-xl" defaultValue="" onChange={(e) => e.target.value && navigate(`/lich/${e.target.value}`)}>
              <option value="">— Chọn tủ —</option>
              {cabinets.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.routeName} / {c.name} ({c.code}) — giao thức v{c.protocolVer}
                </option>
              ))}
            </select>
          )}
        </Card>
      </div>
    );
  }

  if (schedule.isLoading || !outputs) return <Spinner label="Đang tải lịch của tủ…" />;
  if (schedule.isError) return <ErrorBox error={schedule.error} onRetry={() => void schedule.refetch()} />;

  const out = outputs[activeOutput];
  const enabledTimers = out.timers.filter((t) => t.enabled);
  const visibleTimers = showAllSlots ? out.timers : enabledTimers;

  return (
    <div className="space-y-4">
      {/*
        * ══════════════════════════════════════════════════════════════════════════════════
        * ➕ `GD-07B` — TIÊU ĐỀ NÓI RA TÊN TỦ, và ô chọn tủ đứng ngay cạnh nó.
        *
        * 🔴 Đây là trang DUY NHẤT trong app mà một thao tác đổi hành vi của MỘT con tủ cụ thể
        *   ngoài hiện trường. Một tiêu đề "Lịch đóng cắt" trơ trọi trên một trang như thế là
        *   chỗ để người ta soạn nhầm lịch cho tủ bên cạnh — và phát hiện ra bằng cách đi xem
        *   cái đèn. Tên tủ phải nằm trong tiêu đề, không nằm trong một ô chọn ở góc.
        * ══════════════════════════════════════════════════════════════════════════════════
        */}
      <div className="flex flex-wrap items-end gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-nut bg-nhan/15 text-nhan">
          <CalendarClock className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h1 className="chu-tieude m-0 text-[26px] font-semibold leading-tight text-chu-1">
            Lịch đóng cắt — {currentCabinet?.name ?? 'tủ'}
          </h1>
          <p className="mt-0.5 truncate text-[12.5px] text-chu-2">
            {currentCabinet
              ? <>{currentCabinet.routeName} · <span className="so-do">{currentCabinet.code}</span></>
              : 'chưa đọc được tủ này trong cây tuyến'}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
            <select
              className="inp max-w-xs"
              value={cabinetId}
              onChange={(e) => { setDirty(false); navigate(`/lich/${e.target.value}`); }}
            >
              {cabinets.map((c) => (
                <option key={c.id} value={c.id}>{c.routeName} / {c.name}</option>
              ))}
            </select>
            {/*
              🔴 "Đọc lại từ tủ" KHÁC "tải lại trang". Tải lại trang cho ta bản MÁY CHỦ đang
              giữ — *cái ta đã gửi*. Nút này hỏi thẳng tủ — *cái tủ đang chạy*. Hai bản có thể
              lệch nhau mà không ai sai: người trực bấm sửa timer trên màn hình tủ, hoặc một
              lần đẩy trước rớt giữa chừng. Chỉ bản thứ hai bật đèn.

              ⚠ Vô hiệu hoá khi có thay đổi chưa lưu: đọc về sẽ ĐÈ LÊN cái người ta đang soạn.
            */}
            <Button
              variant="secondary"
              loading={docTuTu.isPending}
              disabled={protocolVer < 2 || dirty}
              onClick={() => docTuTu.mutate()}
              title={protocolVer < 2 ? 'Giao thức v1 không có đường đọc lịch ngược lên'
                   : dirty ? 'Lưu hoặc bỏ thay đổi trước — đọc về sẽ đè lên bản đang soạn' : undefined}
            >
              Đọc lại lịch từ tủ
            </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={protocolVer >= 2 ? 'green' : 'amber'}>Giao thức v{protocolVer}</Badge>
        {preview && <Badge tone="slate">{preview.usedSlots}/48 khe v1 đã dùng</Badge>}
        {dirty && (
          <Badge tone="amber">
            {soKheDaSua === null ? 'Có thay đổi chưa lưu' : `Đã sửa ${soKheDaSua} khe — chưa đẩy`}
          </Badge>
        )}
        {schedule.data && <span className="text-xs text-chu-2">Bản đang lưu: {schedule.data.schedule.revision}</span>}
      </div>

      {/* Băng vàng thường trực: mất mát khi hạ v1 của bản ĐANG LƯU */}
      {preview && preview.losses.length > 0 && (
        <WarningBanner
          tone="amber"
          title={`Bản lịch đang lưu có ${preview.losses.length} mục KHÔNG truyền trọn vẹn được bằng giao thức v1`}
          items={preview.losses}
        >
          <p className="mt-2 text-xs">
            {protocolVer >= 2
              ? 'Tủ này chạy giao thức v2 nên vẫn nhận đủ; danh sách trên chỉ để tham khảo nếu sau này thay tủ đời cũ.'
              : 'Tủ này chạy giao thức v1 — những mục trên sẽ KHÔNG xuống tới tủ.'}
          </p>
        </WarningBanner>
      )}

      {/*
        * 🔴 TỦ TỪ CHỐI — hiện NGAY TẠI ĐÂY, không phải trong một bảng nhật ký ở trang khác.
        *   Và KHÔNG tự tắt: đây là kết quả của việc người ta vừa bấm, không phải một thông
        *   báo trôi qua. `detail` in NGUYÊN VĂN vì nó mang GIÁ TRỊ bị từ chối — cắt gọn cho
        *   đẹp là bỏ đúng con số cần đọc.
        */}
      {tuChoi.length > 0 && (
        <div className="rounded-lg border border-nguy bg-nguy-nen px-3 py-2.5 text-sm text-nguy">
          <div className="mb-1.5 flex items-center gap-2">
            <b className="text-chu-1">Tủ TỪ CHỐI {tuChoi.length}/{dangCho.length} khung lịch</b>
            <span className="text-xs text-chu-2">— lịch trên tủ GIỮ NGUYÊN bản cũ</span>
            <button
              type="button" className="ml-auto text-xs underline hover:text-chu-1"
              onClick={() => setTuChoi([])}
            >Đóng</button>
          </div>
          <ul className="space-y-1">
            {tuChoi.map((t) => (
              <li key={t.commandId} className="font-mono text-xs leading-relaxed">
                <span className="text-canh">{t.ten}</span> · {t.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Một băng hổ phách — cùng kiểu với mọi chỗ khoá khác trong app (`components/Khoa`). */}
      <BangKhoaTuyen qd={qdSuaLich} co="canEditSchedule" />

      {/*
        * ══════════════════════════════════════════════════════════════════════════════════
        * BA THẺ NGÕ RA (`GD-07B` mục 3) — và chấm "đang bật" là chỗ Luật số 0 dễ trượt nhất
        * của cả trang.
        *
        * 🔴 CÁI ĐANG SOẠN ≠ CÁI ĐANG CHẠY. `outputs` là bản đang SOẠN trên máy chủ; `isOn` /
        *   `manualHold` là thứ TỦ đang làm ngoài hiện trường, đọc từ cây tuyến. Vẽ trạng thái
        *   chạy từ bản soạn là nói rằng đèn đã đổi trong khi lịch còn chưa rời máy tính.
        * ⛔ VÀ TỦ KHÔNG KHAI THÌ KHÔNG CÓ CHẤM — `null`, không phải "đang tắt". Một chấm xám
        *   đọc thành *"ngõ này đang tắt"*, mà sự thật là *"tôi không biết"*. Hai câu khác nhau
        *   dẫn tới hai hành động khác nhau: một cái thì đi bật, một cái thì đi hỏi.
        * ══════════════════════════════════════════════════════════════════════════════════
        */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {outputs.map((o, i) => {
          /* Ngõ ra TƯƠNG ỨNG do TỦ khai — `undefined` khi tủ chưa khai ngõ ấy (Luật số 0). */
          const song = (currentCabinet?.outputs ?? []).find((x) => x.index === o.index);
          return (
            <button
              key={o.index}
              onClick={() => setActiveOutput(i)}
              className={[
                'shrink-0 rounded-lg border px-3 py-2 text-left transition',
                i === activeOutput ? 'border-nhan-dam bg-nhan/15' : 'border-vien bg-nen-1 hover:bg-nen-2',
              ].join(' ')}
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-chu-1">
                {song === undefined
                  ? <span className="h-2 w-2 shrink-0 rounded-full bg-chua-ro" title="Tủ chưa khai ngõ ra này — KHÔNG phải đang tắt" />
                  : <StatusDot online={song.isOn} pulse={song.isOn} />}
                {o.name || `Ngõ ra ${o.index}`}
              </span>
              <span className="block text-xs text-chu-2">
                {/*
                  * ⚠ "GIỮ TAY" ĐỌC TỪ TỦ (`manualHold`), không từ chế độ đang soạn: một ngõ
                  *   đang bị giữ tay thì lịch mới xuống cũng KHÔNG đổi được cái đèn, và người
                  *   soạn phải biết điều đó TRƯỚC khi soạn xong.
                  */}
                {song?.manualHold
                  ? 'Giữ tay'
                  : `${MODE_LABEL[o.mode]} · ${o.timers.filter((t) => t.enabled).length} timer`}
                {!o.enabled && ' · đang khoá'}
              </span>
            </button>
          );
        })}
      </div>

      {/* Cấu hình ngõ ra */}
      <Card title={`Cấu hình ngõ ra ${out.index}`}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className="lbl">Tên ngõ ra</span>
            <input
              className="inp"
              value={out.name}
              disabled={!canEdit}
              onChange={(e) => patchOutput(activeOutput, { name: e.target.value })}
            />
          </label>

          <label className="block">
            <span className="lbl">Chế độ hoạt động</span>
            <select
              className="inp"
              value={out.mode}
              disabled={!canEdit}
              onChange={(e) => patchOutput(activeOutput, { mode: e.target.value as OutputMode })}
            >
              {modesTheoTu(protocolVer).map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
            </select>
          </label>

          {/*
            ⛔ Ô nào tủ KHÔNG LÀM ĐƯỢC thì KHOÁ, đừng chỉ cảnh báo. Một ô gõ được, lưu được,
            rồi không có tác dụng là đúng cái bẫy `inputEn`/`outputEn` — người bấm tin nó,
            và chỉ phát hiện ra bằng cách đi xem cái đèn.

            Khoá theo BẢN KHAI (`kha.offsetMua`), không theo `protocolVer`: một con tủ nâng
            firmware là ô mở ra ngay, không phải chờ ai sửa mã.
          */}
          <label className="block">
            <span className="lbl">
              Lệch giờ bật so với hoàng hôn (phút)
              {/*
                * ⚠ `GD-07B` — NHÃN VIẾT HOA ĐÚNG CHỮ PHIẾU: "TỦ KHÔNG HỖ TRỢ". Chữ thường
                *   trong một dòng nhãn dài đọc lướt qua như một ghi chú; đây không phải ghi
                *   chú, đây là lý do ô bên dưới không bấm được. Nợ firmware: `NỢ-LICH-01`.
                */}
              {!choSuaOffsetMua && <span className="ml-1 font-semibold text-canh">· TỦ KHÔNG HỖ TRỢ</span>}
            </span>
            <input
              type="number"
              className="inp"
              min={-180}
              max={180}
              value={out.sunsetOffsetMin}
              disabled={!canEdit || out.mode !== 'SEASON' || !choSuaOffsetMua}
              onChange={(e) => patchOutput(activeOutput, { sunsetOffsetMin: clampOffset(e.target.value) })}
            />
          </label>

          <label className="block">
            <span className="lbl">
              Lệch giờ tắt so với bình minh (phút)
              {!choSuaOffsetMua && <span className="ml-1 font-semibold text-canh">· TỦ KHÔNG HỖ TRỢ</span>}
            </span>
            <input
              type="number"
              className="inp"
              min={-180}
              max={180}
              value={out.sunriseOffsetMin}
              disabled={!canEdit || out.mode !== 'SEASON' || !choSuaOffsetMua}
              onChange={(e) => patchOutput(activeOutput, { sunriseOffsetMin: clampOffset(e.target.value) })}
            />
          </label>
        </div>

        <label className="mt-3 flex items-center gap-2 text-sm text-chu-1">
          <input
            type="checkbox"
            className="h-4 w-4 accent-nhan"
            checked={out.enabled}
            disabled={!canEdit}
            onChange={(e) => patchOutput(activeOutput, { enabled: e.target.checked })}
          />
          Cho phép ngõ ra này hoạt động
        </label>

        {/*
          ⛔ OFFSET MÙA KHÔNG CHẠY Ở CẢ HAI PHIÊN BẢN — nhưng vì HAI lý do khác nhau, nên nói
          hai câu khác nhau. Gộp thành một câu "không hỗ trợ" là lấy mất của người đọc thứ
          quyết định họ làm gì tiếp: v1 là chuyện đường truyền (nâng giao thức thì có), v2 là
          chuyện TỦ KHÔNG CÓ CHỖ LƯU (chốt YC-25 mục 3 — phải thêm khoá NVS mới, lee quyết).
        */}
        {out.mode === 'SEASON' && !choSuaOffsetMua && (out.sunsetOffsetMin !== 0 || out.sunriseOffsetMin !== 0) && (
          <p className="mt-2 text-xs text-canh">
            {protocolVer < 2
              ? 'Tủ v1 không nhận được offset mùa — tủ sẽ bật/tắt đúng giờ mặt trời, không lệch phút nào.'
              : 'Tủ này KHAI BÁO là không lưu được offset mùa (supportsSeasonOffset = 0) — nó sẽ bật/tắt đúng giờ mặt trời, không lệch phút nào. Con số dưới đây chỉ nằm trên máy chủ.'}
          </p>
        )}
        {(out.mode === 'ALWAYS_ON' || out.mode === 'ALWAYS_OFF') && protocolVer < 2 && (
          <p className="mt-2 text-xs text-canh">
            Tủ v1 không có chế độ {MODE_LABEL[out.mode]} — server sẽ quy đổi thành
            {out.mode === 'ALWAYS_ON' ? ' timer 00:00–23:59.' : ' không timer nào.'}
          </p>
        )}
      </Card>

      {/* Danh sách timer */}
      <Card
        title={`Timer của ngõ ra ${out.index}`}
        subtitle={`${enabledTimers.length}/48 khe đang dùng`}
        actions={
          <>
            <Button size="sm" onClick={() => setShowAllSlots((v) => !v)}>
              {showAllSlots ? 'Chỉ hiện khe đang dùng' : 'Hiện đủ 48 khe'}
            </Button>
            {canEdit && (
              <Button size="sm" variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => addTimer(activeOutput)}>
                Thêm timer
              </Button>
            )}
          </>
        }
      >
        {visibleTimers.length === 0 ? (
          <EmptyState
            icon={<CalendarClock className="h-8 w-8" />}
            title="Ngõ ra này chưa có timer nào"
            hint="Bấm “Thêm timer” để tạo khoảng giờ bật/tắt đầu tiên."
          />
        ) : (
          <div className="space-y-2">
            {visibleTimers.map((t) => (
              <TimerRow
                key={t.index}
                timer={t}
                canEdit={canEdit}
                protocolVer={protocolVer}
                choDungNgayDacBiet={choDungNgayDacBiet}
                dangKeo={dangKeo}
                cauChan={cauKhoaTuyen(tuyenCuaTu === null ? 'khong-co-tuyen' : qdSuaLich, 'canEditSchedule')}
                onKeo={setDangKeo}
                onTha={(toiIdx) => doiChoKhe(activeOutput, dangKeo ?? toiIdx, toiIdx)}
                onChange={(patch) => patchTimer(activeOutput, t.index, patch)}
                onRemove={() => removeTimer(activeOutput, t.index)}
                onEditSpecial={() => setSpecialFor({ outIdx: activeOutput, timerIdx: t.index })}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Thanh hành động dính đáy */}
      {canEdit && (
        <div className="sticky bottom-2 z-10">
          <div className="card flex flex-wrap items-center justify-between gap-2 p-3">
            {/*
              * ══════════════════════════════════════════════════════════════════════════════
              * THANH TRẠNG THÁI THAY ĐỔI (`GD-07B` mục 6) — MỘT CON SỐ, không phải một cái cờ.
              *
              * 🔴 "Có thay đổi chưa lưu" là câu người đọc KHÔNG kiểm lại được: trang này có 3
              *   ngõ ra × 48 khe, và muốn biết mình vừa động vào cái gì thì phải rà 144 khe.
              *   Sửa 1 khe mà thanh này báo 7 là lúc người ta biết mình vừa lỡ tay — và đó là
              *   TRƯỚC khi đẩy xuống tủ, chỗ duy nhất còn sửa được không tốn gì.
              * ⛔ `soKheDaSua === null` = CHƯA ĐỌC ĐƯỢC BẢN ĐANG LƯU, không phải "không có thay
              *   đổi nào". Lúc ấy `dirty` vẫn là thứ nói thật được, nên nói bằng `dirty`.
              * ⚠ VÀ HAI CỜ PHẢI KHỚP NHAU: `dirty` bật mà đếm ra 0 nghĩa là người dùng đã sửa
              *   rồi sửa về y như cũ — nói thẳng ra như thế, đừng im lặng chọn một trong hai.
              * ══════════════════════════════════════════════════════════════════════════════
              */}
            <p className="flex items-center gap-1.5 text-xs">
              {soKheDaSua === null ? (
                <span className="text-chua-ro">
                  {dirty ? '⚠ Có thay đổi chưa lưu — chưa đọc được bản đang lưu để đếm.'
                         : '— Chưa đọc được bản đang lưu để so.'}
                </span>
              ) : soKheDaSua === 0 ? (
                <span className="text-tot">
                  ✔ Chưa có thay đổi
                  {dirty && <span className="ml-1 text-chu-2">(đã sửa rồi sửa về như cũ)</span>}
                </span>
              ) : (
                <span className="text-canh">
                  ⚠ Đã sửa {soKheDaSua} khe — <b>chưa đẩy xuống tủ</b>
                </span>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {/*
                * ═══════════════════════════════════════════════════════════════════════════
                * ➕ `GD-09` mục 5 — NÚT **BỎ SỬA**. dev_device đi thử 14/09: đang sửa dở thì
                * "Đọc lại lịch từ tủ" MỜ (đúng — đọc về sẽ đè lên bản đang soạn), mà lại KHÔNG
                * có đường nào bỏ bản nháp ⇒ muốn huỷ thì phải sửa ngược lại từng khe một.
                *
                * 🔴 MỘT NGÕ CỤT DO CHÍNH TÔI DỰNG: cái `disabled` kia là một chốt ĐÚNG, và nó
                *   đúng vì có một lối ra — tôi chặn lối vào mà quên mở lối ra. Bản Cài đặt tủ
                *   đã có đúng cặp ấy từ `GD-03` ("đã sửa N ô" + Bỏ sửa); trang Lịch thiếu nửa
                *   thứ hai, và không phép nào bắt được vì nó là một thứ VẮNG MẶT.
                * ⛔ BỎ SỬA = NẠP LẠI BẢN MÁY CHỦ ĐANG GIỮ, không phải "xoá hết khe". Hai việc
                *   ấy khác hẳn nhau, và cái thứ hai là cách nhanh nhất để mất một bản lịch.
                * ⚠ KHÔNG hỏi xác nhận: thứ bị bỏ là bản nháp TRÊN MÀN HÌNH, chưa rời máy tính,
                *   và con số "đã sửa N khe" ngay bên trái đã nói rõ sắp mất gì. Một hộp xác
                *   nhận cho một việc hoàn tác được là một hộp người ta học cách bấm qua.
                * ═══════════════════════════════════════════════════════════════════════════
                */}
              <Button
                icon={<RotateCcw className="h-4 w-4" />}
                disabled={!dirty}
                onClick={() => {
                  if (!schedule.data) return;
                  setOutputs(normalize(schedule.data.schedule.outputs));
                  setPreview(schedule.data.v1Preview);
                  setDirty(false);
                  setTuChoi([]);
                  toast.success('Đã bỏ các thay đổi chưa lưu',
                    'Màn hình trở lại đúng bản lịch máy chủ đang giữ.');
                }}
              >
                Bỏ sửa
              </Button>
              <Button
                icon={<Save className="h-4 w-4" />}
                loading={saveDraft.isPending}
                onClick={() => saveDraft.mutate()}
              >
                Lưu nháp
              </Button>
              <Button
                variant="primary"
                icon={<Upload className="h-4 w-4" />}
                loading={applySchedule.isPending}
                onClick={() => applySchedule.mutate(false)}
              >
                Đẩy xuống tủ
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lịch sử phiên bản */}
      <Card
        title="Lịch sử phiên bản lịch"
        subtitle="Bấm “Lùi lại” để nạp lại bản cũ và đẩy ngay xuống tủ"
        actions={<History className="h-4 w-4 text-chu-3" />}
      >
        {history.isLoading && <Spinner />}
        {history.data && history.data.length === 0 && (
          <EmptyState title="Chưa có bản lịch nào được đẩy xuống tủ" />
        )}
        {history.data && history.data.length > 0 && (
          // Trần RỘNG HƠN chỗ khác, cố ý: bảng lịch đọc bằng cách so hàng với hàng.
          // Xem `.cuon-doc-rong` trong index.css.
          <div className="tbl-wrap cuon-doc-rong">
            <table className="tbl tbl-cuon">
              <thead>
                <tr>
                  <th>Thời điểm tạo</th>
                  <th>Phiên bản</th>
                  <th>Trạng thái</th>
                  <th>Đẩy bằng</th>
                  <th>Ghi chú mất mát</th>
                  <th>Đã áp dụng</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {history.data.map((h) => (
                  <tr key={h.id}>
                    <td className="whitespace-nowrap text-chu-2">{fmtDateTime(h.createdAt)}</td>
                    <td className="font-mono text-xs text-chu-1">{h.revision}</td>
                    <td>
                      <Badge tone={h.state === 'APPLIED' ? 'green' : h.state === 'FAILED' ? 'red' : h.state === 'PENDING' ? 'amber' : 'slate'}>
                        {SCHEDULE_STATE_LABEL[h.state]}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap text-chu-2">v{h.wireVer}</td>
                    <td className="max-w-[22rem] text-xs text-canh" title={h.lossyNote ?? ''}>
                      {h.lossyNote ?? <span className="text-chu-3">không</span>}
                    </td>
                    <td className="whitespace-nowrap text-chu-2">{h.appliedAt ? fmtDateTime(h.appliedAt) : '—'}</td>
                    <td className="text-right">
                      {canEdit && (
                        <Button
                          size="sm"
                          icon={<RotateCcw className="h-3.5 w-3.5" />}
                          loading={rollback.isPending && rollback.variables === h.id}
                          onClick={() => rollback.mutate(h.id)}
                        >
                          Lùi lại
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Hộp thoại xác nhận mất mát khi hạ v1 (HTTP 409) */}
      <Modal
        open={!!pendingLosses}
        onClose={() => setPendingLosses(null)}
        title="Lịch này không truyền trọn vẹn được bằng giao thức v1"
        subtitle="Đọc kỹ từng dòng dưới đây trước khi quyết định"
        danger
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingLosses(null)} disabled={applySchedule.isPending}>
              Để tôi sửa lại lịch
            </Button>
            <Button
              variant="danger"
              loading={applySchedule.isPending}
              onClick={() => applySchedule.mutate(true)}
            >
              Tôi hiểu, vẫn đẩy
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-chu-1">
            Tủ <b>{currentCabinet?.name}</b> đang chạy firmware giao thức v1. Nếu vẫn đẩy, những mục
            sau đây sẽ <b className="text-nguy">KHÔNG</b> xuống được tới tủ:
          </p>
          <ol className="list-decimal space-y-2 rounded-lg border border-nguy/50 bg-nguy-nen p-3 pl-7 text-sm text-nguy">
            {(pendingLosses ?? []).map((l, i) => <li key={i}>{l}</li>)}
          </ol>
          <p className="text-xs text-chu-2">
            Phần truyền được vẫn sẽ xuống tủ bình thường. Bản lịch đầy đủ vẫn được giữ nguyên trên
            máy chủ, nên khi nâng cấp firmware lên v2 chỉ cần đẩy lại là tủ nhận đủ.
          </p>
        </div>
      </Modal>

      {/* Hộp thoại sửa ngày đặc biệt */}
      <SpecialDaysModal
        open={!!specialFor}
        canEdit={canEdit}
        timer={
          specialFor
            ? outputs[specialFor.outIdx].timers.find((t) => t.index === specialFor.timerIdx) ?? null
            : null
        }
        onClose={() => setSpecialFor(null)}
        onSave={(days) => {
          if (specialFor) patchTimer(specialFor.outIdx, specialFor.timerIdx, { specialDays: days });
          setSpecialFor(null);
        }}
      />
    </div>
  );
}

/* --------------------------------------------------------------- helpers */

/** Bảo đảm luôn có đủ 3 ngõ ra × 48 timer để trình soạn không phải kiểm tra null khắp nơi. */
function normalize(outputs: FullOutput[]): FullOutput[] {
  return [1, 2, 3].map((idx) => {
    const src = outputs.find((o) => o.index === idx);
    const timers: FullTimer[] = Array.from({ length: 48 }, (_, i) => {
      const t = src?.timers.find((x) => x.index === i);
      return t
        ? { ...t, specialDays: Array.isArray(t.specialDays) ? t.specialDays : [] }
        : { index: i, enabled: false, onMin: 0, offMin: 0, daysOfWeek: '0000000', specialDays: [] };
    });
    return {
      index: idx,
      name: src?.name ?? `Ngõ ra ${idx}`,
      enabled: src?.enabled ?? true,
      mode: src?.mode ?? 'TIMER',
      sunsetOffsetMin: src?.sunsetOffsetMin ?? 0,
      sunriseOffsetMin: src?.sunriseOffsetMin ?? 0,
      timers,
    };
  });
}

function clampOffset(v: string): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(-180, Math.min(180, Math.round(n)));
}

/* ------------------------------------------------------------ một timer */

function TimerRow({
  timer, canEdit, protocolVer, choDungNgayDacBiet, dangKeo, cauChan, onKeo, onTha,
  onChange, onRemove, onEditSpecial,
}: {
  timer: FullTimer;
  canEdit: boolean;
  protocolVer: number;
  /// Bản khai của tủ (`kha.ngayDacBiet`), KHÔNG suy từ `protocolVer`.
  choDungNgayDacBiet: boolean;
  /// Số KHE đang được kéo (`null` = không kéo) — để tô chỗ sắp thả.
  dangKeo: number | null;
  /// Câu giải thích vì sao không sửa được — do `cauChanCoTuyen()` dựng, KHÔNG tự viết.
  cauChan: string;
  onKeo: (kheIdx: number | null) => void;
  onTha: (kheIdx: number) => void;
  onChange: (patch: Partial<FullTimer>) => void;
  onRemove: () => void;
  onEditSpecial: () => void;
}) {
  const dim = !timer.enabled;
  const noDay = timer.enabled && timer.daysOfWeek === '0000000';
  const sameTime = timer.enabled && timer.onMin === timer.offMin;

  /* Chỗ sắp thả — tô để người kéo thấy mình sắp tráo với ô nào, TRƯỚC khi buông chuột. */
  const dichDen = dangKeo !== null && dangKeo !== timer.index;

  return (
    <div
      /*
       * ⚠ KÉO THẢ GỐC CỦA TRÌNH DUYỆT — không thêm gói (`GD-07B` mục 5 cho phép).
       * ⛔ CHỈ KÉO ĐƯỢC KHI CÓ QUYỀN SỬA: một thao tác kéo mượt mà rồi không đổi được gì là
       *   một lời hứa suông, và ở đây nó còn làm người ta tưởng đã sắp lại xong.
       */
      draggable={canEdit}
      onDragStart={(e) => { onKeo(timer.index); e.dataTransfer.effectAllowed = 'move'; }}
      onDragEnd={() => onKeo(null)}
      onDragOver={(e) => { if (canEdit && dangKeo !== null) e.preventDefault(); }}
      onDrop={(e) => { e.preventDefault(); if (canEdit && dangKeo !== null) onTha(timer.index); onKeo(null); }}
      className={[
        'rounded-xl border p-3 transition',
        dim ? 'border-vien bg-nen-2/60' : 'border-vien bg-nen-1',
        dangKeo === timer.index ? 'opacity-50' : '',
        dichDen ? 'border-nhan/60' : '',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-center gap-3">
        {/*
          * ⚠ TAY KÉO LÀ MỘT BIỂU TƯỢNG, không phải cả cái thẻ: kéo được ở mọi chỗ thì người ta
          *   quét chọn giờ cũng thành kéo thẻ. `cursor-grab` nói ra chỗ cầm được.
          */}
        <span
          aria-hidden="true"
          /*
           * ⛔ CÂU BỊ CHẶN LẤY TỪ `cauChanCoTuyen()`, KHÔNG TỰ VIẾT. Bản đầu tôi gõ tay
           *   *"Không có quyền sửa lịch tuyến này"* và bộ kiểm đỏ đúng chỗ: một tình huống
           *   thì một câu, và câu ấy chỉ được dựng ở MỘT nơi. Tự viết thêm một câu cho cùng
           *   tình huống nghĩa là hai chỗ sẽ nói hai kiểu — và bốn ca của `cauChanCoTuyen`
           *   (chưa gán tuyến · thiếu cờ · tủ chưa có tuyến) bị gộp thành một câu chung chung
           *   đúng ở chỗ tôi vừa gõ, sai ở ba ca còn lại.
           */
          title={canEdit ? 'Kéo để tráo khe với một khe khác' : cauChan}
          className={`shrink-0 ${canEdit ? 'cursor-grab text-chu-3' : 'cursor-not-allowed text-chua-ro'}`}
        >
          <GripVertical className="h-4 w-4" />
        </span>
        <span className="flex items-center gap-2">
          {/*
            * ⚠ `GD-07B` — CÔNG TẮC thay ô tick (mẫu `_BUS/27`). Cùng một việc, cùng một dữ
            *   liệu; chỉ khác ở chỗ công tắc đọc được trạng thái từ xa hơn một ô vuông nhỏ.
            */}
          <Switch
            checked={timer.enabled}
            disabled={!canEdit}
            onChange={(v) => onChange({ enabled: v })}
            labelOn="BẬT" labelOff="TẮT"
          />
          <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-chu-2">
            Khe {timer.index}
          </span>
        </span>

        <div className="flex items-center gap-2">
          <span className="text-xs text-chu-2">Bật</span>
          <TimeInput
            value={timer.onMin}
            disabled={!canEdit || !timer.enabled}
            onChange={(m) => onChange({ onMin: m })}
            className="w-28"
          />
          <span className="text-xs text-chu-2">Tắt</span>
          <TimeInput
            value={timer.offMin}
            disabled={!canEdit || !timer.enabled}
            onChange={(m) => onChange({ offMin: m })}
            className="w-28"
          />
        </div>

        {timer.enabled && !sameTime && (
          <Badge tone="slate">
            {minToStr(timer.onMin)}–{minToStr(timer.offMin)} · {fmtDuration(durationMin(timer.onMin, timer.offMin))}
          </Badge>
        )}

        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            icon={<CalendarDays className="h-4 w-4" />}
            onClick={onEditSpecial}
            disabled={!timer.enabled}
            title="Ngày đặc biệt (tối đa 10)"
          >
            {timer.specialDays.length > 0 ? `${timer.specialDays.length} ngày đặc biệt` : 'Ngày đặc biệt'}
          </Button>
          {canEdit && timer.enabled && (
            <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4" />} onClick={onRemove} title="Xoá timer">
              <span className="sr-only">Xoá</span>
            </Button>
          )}
        </div>
      </div>

      {/* 7 ô chọn thứ — index 0 = Chủ nhật, đúng thứ tự firmware */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {DAY_LABELS.map((d, i) => {
          const active = timer.daysOfWeek[i] === '1';
          return (
            <button
              key={d}
              type="button"
              disabled={!canEdit || !timer.enabled}
              title={DAY_LABELS_FULL[i]}
              onClick={() => onChange({ daysOfWeek: toggleDay(timer.daysOfWeek, i) })}
              className={[
                'h-8 w-10 rounded-lg border text-xs font-semibold transition',
                active ? 'border-nhan bg-nhan text-chu-1' : 'border-vien bg-nen-1 text-chu-2',
                !canEdit || !timer.enabled ? 'cursor-not-allowed opacity-60' : 'hover:border-nhan/50',
              ].join(' ')}
            >
              {d}
            </button>
          );
        })}
        {/*
          * ⚠ "CẢ TUẦN" LÀ MỘT NÚT GẠT HAI CHIỀU, không phải một nút "chọn hết". Bấm lần nữa
          *   phải BỎ hết — nếu không thì người lỡ tay không có đường lui nào ngoài bấm bảy lần.
          * ⛔ Và ca "bỏ hết" dẫn thẳng tới câu cảnh báo đã có ngay dưới (*"timer này sẽ không
          *   bao giờ chạy"*) — không giấu, không tự chặn.
          */}
        <button
          type="button"
          disabled={!canEdit || !timer.enabled}
          onClick={() => onChange({ daysOfWeek: timer.daysOfWeek === ALL_DAYS ? NO_DAYS : ALL_DAYS })}
          className={[
            'h-8 rounded-lg border px-2.5 text-xs font-semibold transition',
            timer.daysOfWeek === ALL_DAYS ? 'border-nhan bg-nhan/15 text-nhan' : 'border-vien bg-nen-1 text-chu-2',
            !canEdit || !timer.enabled ? 'cursor-not-allowed opacity-60' : 'hover:border-nhan/50',
          ].join(' ')}
        >
          Cả tuần
        </button>
        <span className="ml-1 text-xs text-chu-2">{describeDays(timer.daysOfWeek)}</span>

        {timer.specialDays.length > 0 && (
          <span className="ml-2 flex flex-wrap gap-1">
            {timer.specialDays.map((sd, i) => (
              <Badge key={i} tone="blue">{fmtSpecialDay(sd)}</Badge>
            ))}
          </span>
        )}
      </div>

      {/* Cảnh báo tại chỗ */}
      {noDay && (
        <p className="mt-2 flex items-center gap-1 text-xs text-canh">
          <AlertTriangle className="h-3.5 w-3.5" /> Chưa chọn thứ nào — timer này sẽ không bao giờ chạy.
        </p>
      )}
      {sameTime && (
        <p className="mt-2 flex items-center gap-1 text-xs text-canh">
          <AlertTriangle className="h-3.5 w-3.5" /> Giờ bật trùng giờ tắt — khe này coi như trống.
        </p>
      )}
      {protocolVer < 2 && timer.enabled && timer.daysOfWeek !== ALL_DAYS && timer.daysOfWeek !== '0000000' && (
        <p className="mt-1 text-xs text-canh">
          Tủ v1 sẽ chạy timer này CẢ 7 NGÀY vì không nhận được bộ lọc thứ trong tuần.
        </p>
      )}
      {/*
        Hai câu KHÁC NHAU cho hai lý do khác nhau — đừng gộp thành "không hỗ trợ":
          v1        → chuyện ĐƯỜNG TRUYỀN (nâng giao thức thì có)
          bản khai  → chuyện CON TỦ NÀY (nâng firmware con tủ này thì có)
        Người đọc cần biết mình phải đi làm gì, không chỉ biết là không chạy.
      */}
      {timer.enabled && timer.specialDays.length > 0 && protocolVer < 2 && (
        <p className="mt-1 text-xs text-canh">Tủ v1 sẽ bỏ qua toàn bộ ngày đặc biệt của timer này.</p>
      )}
      {timer.enabled && timer.specialDays.length > 0 && protocolVer >= 2 && !choDungNgayDacBiet && (
        <p className="mt-1 text-xs text-canh">
          Tủ này KHAI BÁO là không nhận ngày đặc biệt (supportsSpecialDays = 0) — nó sẽ bỏ qua
          toàn bộ ngày đặc biệt của timer này.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------- ngày đặc biệt */

function SpecialDaysModal({
  open, timer, canEdit, onClose, onSave,
}: {
  open: boolean;
  timer: FullTimer | null;
  canEdit: boolean;
  onClose: () => void;
  onSave: (days: SpecialDay[]) => void;
}) {
  const [days, setDays] = useState<SpecialDay[]>([]);

  useEffect(() => {
    if (open && timer) setDays(timer.specialDays.map((d) => ({ ...d })));
  }, [open, timer]);

  const patch = (i: number, p: Partial<SpecialDay>) =>
    setDays((prev) => prev.map((d, k) => (k === i ? { ...d, ...p } : d)));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Ngày đặc biệt — khe timer ${timer?.index ?? ''}`}
      subtitle="Tối đa 10 ngày. Ngày đặc biệt là ngày timer này ĐƯỢC ÁP DỤNG riêng (vd: lễ, Tết)."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Đóng</Button>
          {canEdit && <Button variant="primary" onClick={() => onSave(days.filter((d) => d.d > 0 && d.m > 0))}>Lưu vào timer</Button>}
        </>
      }
    >
      <div className="space-y-2">
        {days.length === 0 && <p className="text-sm text-chu-2">Chưa khai báo ngày đặc biệt nào.</p>}

        {days.map((d, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-6 text-xs text-chu-3">{i + 1}.</span>
            <label className="flex items-center gap-1 text-xs text-chu-2">
              Ngày
              <input
                type="number" min={1} max={31} className="inp w-20"
                value={d.d || ''}
                disabled={!canEdit}
                onChange={(e) => patch(i, { d: Math.max(0, Math.min(31, Number(e.target.value) || 0)) })}
              />
            </label>
            <label className="flex items-center gap-1 text-xs text-chu-2">
              Tháng
              <input
                type="number" min={1} max={12} className="inp w-20"
                value={d.m || ''}
                disabled={!canEdit}
                onChange={(e) => patch(i, { m: Math.max(0, Math.min(12, Number(e.target.value) || 0)) })}
              />
            </label>
            {canEdit && (
              <Button
                size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4" />}
                onClick={() => setDays((prev) => prev.filter((_, k) => k !== i))}
              >
                <span className="sr-only">Xoá dòng</span>
              </Button>
            )}
          </div>
        ))}

        {canEdit && (
          <Button
            size="sm"
            icon={<Plus className="h-4 w-4" />}
            disabled={days.length >= 10}
            onClick={() => setDays((prev) => [...prev, { d: 1, m: 1 }])}
          >
            Thêm ngày ({days.length}/10)
          </Button>
        )}
      </div>
    </Modal>
  );
}
